import { isAssetType } from 'src/data/asset';
import { GENERATION_SOURCE_TYPE } from 'src/data/generation/const';
import { formatToolDisplayName, isGenerationTool } from 'src/data/generationTool';
import {
  getImproverContentTypeTitle,
  type LongFormContentType,
} from 'src/data/improver/contentType';
import {
  AI_CHAT_LABEL,
  BLOG_WIZARD_LABEL,
  CONTENT_IMPROVER_LABEL,
  LOCALIZATION_LABEL,
} from 'src/data/ui';

import { getAssetGroupDescription } from './assetGroupDescription';
import type { FeedAssetGroup, ShortFormFeedAsset } from './types';
import { isLongFormAssetGroup } from './utils';

// Both are imported from their own modules rather than `src/data/generation` and
// `src/data/improver`: those barrels reach back into this one, and the cycle
// only shows up at runtime as an undefined import.

export const LONG_FORM_GROUP_FALLBACK_TITLE = 'Long form';
const SHORT_FORM_GROUP_FALLBACK_TITLE = 'Short form';

interface ShortFormGroupTitle {
  title: string;
  source: string;
}

/**
 * Composed on the FE from the group's request-level fields (mirrors the legacy
 * `GroupTitleDefault` precedence): a sub-template name wins, otherwise the
 * tool/asset description.
 */
export const composeShortFormGroupTitle = (group: FeedAssetGroup): ShortFormGroupTitle => {
  const { title, source } = getAssetGroupDescription(group);

  return {
    title:
      group.subTemplate?.name || title || group.contentLabel || SHORT_FORM_GROUP_FALLBACK_TITLE,
    source: source || '',
  };
};

/**
 * The document's own name. `contentType` is a slug, so it stands in only after
 * being labelled — and only while `contentLabel` is empty, which is the case
 * until generation completes.
 */
export const composeLongFormGroupTitle = (group: FeedAssetGroup): string =>
  group.contentLabel ||
  (group.contentType
    ? getImproverContentTypeTitle(group.contentType as LongFormContentType)
    : '') ||
  LONG_FORM_GROUP_FALLBACK_TITLE;

/**
 * What produced a long-form group, said ahead of its name. A group whose source has no label of its
 * own is named after its document alone — short form says where it came from in the title itself.
 */
export const composeLongFormGroupSource = (group: FeedAssetGroup): string => {
  switch (group.sourceType) {
    case GENERATION_SOURCE_TYPE.aiChat:
      return AI_CHAT_LABEL;
    case GENERATION_SOURCE_TYPE.contentImprover:
      return CONTENT_IMPROVER_LABEL;
    case GENERATION_SOURCE_TYPE.localization:
      return LOCALIZATION_LABEL;
    case GENERATION_SOURCE_TYPE.blogWizard:
      return BLOG_WIZARD_LABEL;
    default:
      return '';
  }
};

/** What the feed shows as a group's name, whichever kind of group it is. */
export const composeFeedGroupTitle = (group: FeedAssetGroup): string =>
  isLongFormAssetGroup(group)
    ? composeLongFormGroupTitle(group)
    : composeShortFormGroupTitle(group).title;

/** Stands in for a set of assets that nothing else names. */
const SAVED_CONTENT_TITLE = 'Content';

/**
 * Names what a save put into the document, for a hint that reads "<this> was saved." A template names
 * the whole group whatever it holds, and what the group was generated with names it after that.
 * Failing both, a lone asset is named as its own card names it, and a set of them is named by
 * neither.
 */
export const composeSavedAssetsTitle = (
  group: FeedAssetGroup,
  assets: ShortFormFeedAsset[]
): string => {
  const templateName = group.subTemplate?.name || group.customTemplate?.name;
  if (templateName) {
    return templateName;
  }

  const { title, namesOutput } = getAssetGroupDescription(group);
  if (namesOutput) {
    return title;
  }

  const asset = assets.length === 1 ? assets[0] : undefined;
  const tool = asset?.generationTool;
  const assetType = asset?.assetType;

  return isGenerationTool(tool) && isAssetType(assetType)
    ? formatToolDisplayName(tool, { assetType })
    : SAVED_CONTENT_TITLE;
};
